import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_BASE_URL } from '@core/injectors';
import { OrganizationDataDTO, OrganizationDetailsDTO, OrganizationItemDTO, SnowflakeUrlData } from '@core/models';
import { AccountService } from '@features/server-side-authentication';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import { UserService } from '../user/user.service';

@Injectable()
export class OrganizationsService {
  private selectedOrganizationSubject = new BehaviorSubject<OrganizationDataDTO | undefined>(undefined);

  constructor(
    private readonly http: HttpClient,
    private accountService: AccountService,
    private userService: UserService,
    @Inject(API_BASE_URL) private readonly apyBaseUrl: string,
  ) {}

  public setSelectedOrganizationData(organizationData: OrganizationDataDTO) {
    this.selectedOrganizationSubject.next(organizationData);
  }

  public getSelectedOrganizationData(): Observable<OrganizationDataDTO | undefined> {
    return this.selectedOrganizationSubject.asObservable();
  }

  public get selectedOrganization(): OrganizationDataDTO | undefined {
    return this.selectedOrganizationSubject.value;
  }

  public get selectedOrganizationId(): number | null {
    return this.selectedOrganization?.pk || null;
  }

  public get selectedOrganizationName(): string {
    return this.selectedOrganization?.name || '<no organization>';
  }

  public get selectedOrganizationShortName(): string {
    return this.selectedOrganization?.short_name || '<no organization>';
  }

  public get selectedOrganizationSnowflakeUrl(): Observable<SnowflakeUrlData | null> {
    return this.getSelectedOrganizationData().pipe(
      map((org) => {
        const snowflake = org?.product_links?.filter((product) => product.link_type === 'Snowflake');
        if (Array.isArray(snowflake) && snowflake?.length) {
          return {
            url: snowflake[0].url,
            use_sso: snowflake[0].use_sso || false,
          };
        } else {
          const userSnowflakeUrl = this.userService.user?.snowflake_url;
          return userSnowflakeUrl?.url ? userSnowflakeUrl : null;
        }
      }),
    );
  }

  public get selectedOrganizationThoughtSpotUrl(): Observable<string | null> {
    return this.getSelectedOrganizationData().pipe(
      map((org) => {
        const thoughtSpot = org?.product_links?.filter((product) => product.link_type === 'ThoughtSpot');
        return Array.isArray(thoughtSpot)
          ? thoughtSpot?.length
            ? thoughtSpot[0].url
            : null
          : this.userService.user?.thoughspot_url || null;
      }),
    );
  }

  public get selectedOrganizationTimeZone(): string {
    return this.selectedOrganization?.timezone || '-0000';
  }

  public get selectedOrganizationTimeZoneShiftMinutes(): number {
    const hours = this.selectedOrganizationTimeZone.substring(0, 3),
      minutes = this.selectedOrganizationTimeZone.substring(3);
    return parseInt(hours, 10) * 60 + parseInt(minutes, 10);
  }

  public getList(): Observable<OrganizationItemDTO[]> {
    return this.http.get<OrganizationItemDTO[]>(`${this.apyBaseUrl}/api/organizations-internal/`);
  }

  public getOrganizationData(): Observable<OrganizationDataDTO> {
    return this.http
      .get<OrganizationDataDTO>(`${this.apyBaseUrl}/api/organization/`)
      .pipe(tap((data) => this.setSelectedOrganizationData(data)));
  }

  public getDashboardData(id: string | null): Observable<OrganizationDetailsDTO> {
    return this.http.get<OrganizationDetailsDTO>(
      `${this.apyBaseUrl}/api/dashboard/` + (id && this.accountService.isInternal ? `?as_customer=${id}` : ''),
    );
  }
}
